import React, { useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPercent, faArrowDown, faArrowUp, faHandshake, faBuilding,
  faCalendarDays, faFileExport, faChevronDown, faChevronRight, faCrown,
  faArrowRightArrowLeft, faMoneyBillWave, faRotate, faDroplet as faDropletSolid,
} from '@fortawesome/free-solid-svg-icons';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import P2pReportPanel, { P2pReportSummary } from '../../components/P2pReportPanel';

const DATE_PICKER_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const PRESET_BTN_CLASS =
  'px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 hover:border-gray-400 dark:hover:border-slate-500 text-gray-600 dark:text-slate-300 transition-colors';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MerchantFeeRow {
  merchant_id: string;
  merchant_name: string;
  fee_percentage: number;
  payout_fee_percentage: number;
  reseller_id: string | null;
  reseller_name: string | null;
  reseller_fee_rate: number;
  reseller_payout_fee_rate: number;
  parent_reseller_id: string | null;
  parent_reseller_name: string | null;
  parent_fee_rate: number;
  parent_payout_fee_rate: number;
  payin_count: number;
  payin_volume: number;
  payin_fee: number;
  payout_count: number;
  payout_volume: number;
  payout_fee: number;
  p2p_fee_payin?: number;
  p2p_fee_payout?: number;
  p2p_fee?: number;
  reseller_payin_share: number;
  parent_payin_share: number;
  owner_payin_share: number;
  reseller_payout_share: number;
  parent_payout_share: number;
  owner_payout_share: number;
  reseller_p2p_share?: number;
  parent_p2p_share?: number;
  owner_p2p_share?: number;
  total_fee: number;
  total_reseller_share: number;
  total_parent_share: number;
  total_owner_share: number;
}

interface ResellerFeeRow {
  reseller_id: string;
  reseller_name: string;
  parent_reseller_id: string | null;
  parent_reseller_name: string | null;
  merchant_count: number;
  payin_count: number;
  payin_volume: number;
  payin_fee: number;
  payout_count: number;
  payout_volume: number;
  payout_fee: number;
  reseller_payin_share: number;
  parent_payin_share: number;
  owner_payin_share: number;
  reseller_payout_share: number;
  parent_payout_share: number;
  owner_payout_share: number;
  total_fee: number;
  total_reseller_share: number;
  total_parent_share: number;
  total_owner_share: number;
}

interface ParentResellerRow {
  reseller_id: string;
  reseller_name: string;
  payin_spread: number;
  payout_spread: number;
  total_spread: number;
}

interface FeeSummary {
  total_payin_count: number;
  total_payin_volume: number;
  total_payin_fee: number;
  total_payout_count: number;
  total_payout_volume: number;
  total_payout_fee: number;
  total_p2p_fee_payin?: number;
  total_p2p_fee_payout?: number;
  total_p2p_fee?: number;
  total_fee: number;
  total_reseller_share: number;
  total_parent_share: number;
  total_owner_share: number;
  merchant_count: number;
  direct_merchant_count: number;
  reseller_merchant_count: number;
}

interface FeeReportData {
  summary: FeeSummary;
  merchants: MerchantFeeRow[];
  by_reseller: ResellerFeeRow[];
  by_parent_reseller: ParentResellerRow[];
  date_from: string;
  date_to: string;
}


// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (v: number | string) =>
  parseFloat(String(v ?? 0)).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtInt = (v: number) => Number(v).toLocaleString('th-TH');

const pct = (part: number, total: number) =>
  total > 0 ? ((part / total) * 100).toFixed(1) + '%' : '0%';

// ─── Quick-date presets ───────────────────────────────────────────────────────

const PRESETS = [
  { label: 'วันนี้',     from: () => dayjs().tz(TZ).startOf('day'),                      to: () => dayjs().tz(TZ).endOf('day') },
  { label: 'เมื่อวาน',   from: () => dayjs().tz(TZ).subtract(1,'day').startOf('day'),    to: () => dayjs().tz(TZ).subtract(1,'day').endOf('day') },
  { label: '7 วัน',      from: () => dayjs().tz(TZ).subtract(6,'day').startOf('day'),    to: () => dayjs().tz(TZ).endOf('day') },
  { label: '30 วัน',     from: () => dayjs().tz(TZ).subtract(29,'day').startOf('day'),   to: () => dayjs().tz(TZ).endOf('day') },
  { label: 'เดือนนี้',   from: () => dayjs().tz(TZ).startOf('month'),                   to: () => dayjs().tz(TZ).endOf('day') },
  { label: 'เดือนก่อน', from: () => dayjs().tz(TZ).subtract(1,'month').startOf('month'),to: () => dayjs().tz(TZ).subtract(1,'month').endOf('month') },
];

// ─── Component ───────────────────────────────────────────────────────────────

const defaultStartDate = () => dayjs().tz(TZ).startOf('day').toDate();
const defaultEndDate = () => dayjs().tz(TZ).endOf('day').toDate();

const FeeReportPage: React.FC = () => {
  const [draftStartDate, setDraftStartDate] = useState<Date>(defaultStartDate);
  const [draftEndDate, setDraftEndDate] = useState<Date>(defaultEndDate);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FeeReportData | null>(null);
  const [p2p, setP2p] = useState<P2pReportSummary | null>(null);
  const [tab, setTab] = useState<'merchant' | 'reseller'>('merchant');
  const [expandedResellers, setExpandedResellers] = useState<Set<string>>(new Set());

  const fetchReport = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    try {
      const params = { date_from: dayjs(from).toISOString(), date_to: dayjs(to).toISOString() };
      // ยิงคู่กัน: report ปกติ + report P2P (แยก) — P2P ล้มไม่กระทบ report หลัก
      const [res, p2pRes] = await Promise.all([
        merchantApi.get('/admin/fee-report', { params }),
        merchantApi.get('/admin/p2p-report', { params }).catch(() => null),
      ]);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        toast.error('โหลดข้อมูลไม่สำเร็จ');
      }
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);
    } catch {
      toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = () => {
    if (dayjs(draftStartDate).isAfter(dayjs(draftEndDate))) {
      toast.error('วันเริ่มต้นต้องน้อยกว่าหรือเท่ากับวันสิ้นสุด');
      return;
    }
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    fetchReport(draftStartDate, draftEndDate);
  };

  const handleReset = () => {
    const start = defaultStartDate();
    const end = defaultEndDate();
    setDraftStartDate(start);
    setDraftEndDate(end);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setData(null);
    setP2p(null);
    setExpandedResellers(new Set());
  };

  const applyPreset = (from: () => dayjs.Dayjs, to: () => dayjs.Dayjs) => {
    const f = from().toDate();
    const t = to().toDate();
    setDraftStartDate(f);
    setDraftEndDate(t);
    setAppliedStartDate(f);
    setAppliedEndDate(t);
    fetchReport(f, t);
  };

  const toggleReseller = (id: string) =>
    setExpandedResellers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['ร้านค้า','ตัวแทน','อัตรา Payin%','จำนวน Payin','Volume Payin','Fee Payin',
       'อัตรา Payout%','จำนวน Payout','Volume Payout','Fee Payout',
       'Fee P2P In','Fee P2P Out','Fee P2P',
       'รวม Fee','ส่วนตัวแทน','ส่วนเจ้าของ'],
      ...data.merchants.map(m => [
        m.merchant_name, m.reseller_name || 'Direct',
        m.fee_percentage, m.payin_count, m.payin_volume, m.payin_fee,
        m.payout_fee_percentage, m.payout_count, m.payout_volume, m.payout_fee,
        m.p2p_fee_payin || 0, m.p2p_fee_payout || 0, m.p2p_fee || 0,
        m.total_fee, m.total_reseller_share, m.total_owner_share,
      ]),
    ];
    const csv  = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    const from = appliedStartDate || draftStartDate;
    const to = appliedEndDate || draftEndDate;
    a.download = `fee_report_${dayjs(from).format('YYYYMMDD')}_${dayjs(to).format('YYYYMMDD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;

  const directFee = s
    ? s.total_fee - (data?.by_reseller ?? []).reduce((acc, r) => acc + r.total_fee, 0)
    : 0;

  const statCards: StatCardDef[] = useMemo(() => {
    if (!s) return [];
    return [
      {
        icon: faArrowDown,
        iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        label: 'Payin Fee',
        value: <span className="text-emerald-600 dark:text-emerald-400">฿{fmt(s.total_payin_fee)}</span>,
        subValue: (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            {fmtInt(s.total_payin_count)} รายการ · Vol ฿{fmt(s.total_payin_volume)}
          </span>
        ),
      },
      {
        icon: faArrowUp,
        iconClassName: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
        label: 'Payout Fee',
        value: <span className="text-rose-600 dark:text-rose-400">฿{fmt(s.total_payout_fee)}</span>,
        subValue: (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            {fmtInt(s.total_payout_count)} รายการ · Vol ฿{fmt(s.total_payout_volume)}
          </span>
        ),
      },
      {
        icon: faDropletSolid,
        iconClassName: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
        label: 'P2P Fee',
        value: <span className="text-sky-600 dark:text-sky-400">฿{fmt(s.total_p2p_fee || 0)}</span>,
        subValue: (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            In ฿{fmt(s.total_p2p_fee_payin || 0)} · Out ฿{fmt(s.total_p2p_fee_payout || 0)}
          </span>
        ),
      },
      {
        icon: faArrowRightArrowLeft,
        iconClassName: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
        label: 'รวม Fee',
        value: <span className="text-violet-600 dark:text-violet-400">฿{fmt(s.total_fee)}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{fmtInt(s.merchant_count)} ร้าน · In + Out + P2P</span>,
      },
      {
        icon: faHandshake,
        iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        label: 'ส่วนตัวแทน (รวม)',
        value: <span className="text-amber-600 dark:text-amber-400">฿{fmt(s.total_reseller_share + (s.total_parent_share || 0))}</span>,
        subValue: (s.total_parent_share || 0) > 0 ? (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            Sub ฿{fmt(s.total_reseller_share)} · Parent ฿{fmt(s.total_parent_share)}
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-slate-500">{pct(s.total_reseller_share, s.total_fee)} ของ Fee รวม</span>
        ),
      },
      {
        icon: faCrown,
        iconClassName: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
        label: 'ส่วนเจ้าของ',
        value: <span className="text-teal-600 dark:text-teal-400">฿{fmt(s.total_owner_share)}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{pct(s.total_owner_share, s.total_fee)} ของ Fee รวม</span>,
      },
    ];
  }, [s]);

  const periodLabel = data
    ? `${dayjs(data.date_from).tz(TZ).format('DD/MM/YYYY HH:mm')} – ${dayjs(data.date_to).tz(TZ).format('DD/MM/YYYY HH:mm')}`
    : appliedStartDate && appliedEndDate
      ? `${dayjs(appliedStartDate).tz(TZ).format('DD/MM/YYYY HH:mm')} – ${dayjs(appliedEndDate).tz(TZ).format('DD/MM/YYYY HH:mm')}`
      : null;

  return (
    <div className="max-w-full overflow-x-hidden">
      <UiPageHeader
        icon={faPercent}
        title="รายงานค่าธรรมเนียม"
        subtitle="Payin · Payout · ส่วนแบ่ง Reseller · กำไรเจ้าของ"
      />

      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <>
            {data && (
              <button
                type="button"
                onClick={exportCSV}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faFileExport} /> Export CSV
              </button>
            )}
            {appliedStartDate && appliedEndDate && (
              <button
                type="button"
                onClick={() => fetchReport(appliedStartDate, appliedEndDate)}
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-wrap items-end gap-4 w-fit">
            <div className="w-[200px] shrink-0 z-30">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">วันเริ่มต้น</label>
              <DatePicker
                selected={draftStartDate}
                onChange={(d: Date | null) => d && setDraftStartDate(d)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="dd/MM/yyyy HH:mm"
                className={DATE_PICKER_CLASS}
                popperProps={{ strategy: 'fixed' }}
                popperClassName="!z-[9999]"
              />
            </div>
            <div className="w-[200px] shrink-0">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">วันสิ้นสุด</label>
              <DatePicker
                selected={draftEndDate}
                onChange={(d: Date | null) => d && setDraftEndDate(d)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="dd/MM/yyyy HH:mm"
                minDate={draftStartDate}
                className={DATE_PICKER_CLASS}
                popperProps={{ strategy: 'fixed' }}
                popperClassName="!z-[9999]"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPreset(p.from, p.to)}
                className={PRESET_BTN_CLASS}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </UiFilterPanel>

      {s && <UiStatsGrid cards={statCards} loading={loading} cols={5} />}

      {/* 💧 P2P — component กลาง (fallback เข้าบัญชีเว็บถูกนับรวมใน summary ด้านบนแล้ว) */}
      <P2pReportPanel summary={p2p} />

      {periodLabel && (
        <p className="text-xs text-gray-400 dark:text-slate-500 mb-4 flex items-center gap-1.5">
          <FontAwesomeIcon icon={faCalendarDays} />
          ช่วงเวลา: {periodLabel}
        </p>
      )}

      {data && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 min-w-0">

          <div className="flex border-b border-gray-100 dark:border-slate-700">
            <button
              type="button"
              onClick={() => setTab('merchant')}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                tab === 'merchant'
                  ? 'border-b-2 text-gray-800 dark:text-slate-100 font-semibold'
                  : 'border-transparent text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300'
              }`}
              style={tab === 'merchant' ? { borderBottomColor: 'var(--theme-primary)' } : undefined}
            >
              <FontAwesomeIcon icon={faBuilding} />
              รายร้านค้า
              <span className="ml-1 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-xs rounded-full px-2 py-0.5">
                {data.merchants.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setTab('reseller')}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                tab === 'reseller'
                  ? 'border-b-2 text-gray-800 dark:text-slate-100 font-semibold'
                  : 'border-transparent text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300'
              }`}
              style={tab === 'reseller' ? { borderBottomColor: 'var(--theme-primary)' } : undefined}
            >
              <FontAwesomeIcon icon={faHandshake} />
              รายตัวแทน
              <span className="ml-1 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-xs rounded-full px-2 py-0.5">
                {data.by_reseller.length}
              </span>
            </button>
          </div>

          {/* ── Tab: รายร้านค้า ── */}
          {tab === 'merchant' && (
            <div className="w-full overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200">
              <table className="w-full text-sm table-auto">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-700 text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide bg-gray-50/60 dark:bg-slate-700/30">
                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">ร้านค้า</th>
                    <th className="px-4 py-3 text-left font-medium whitespace-nowrap">ตัวแทน</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-emerald-600">จำนวน Payin</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-emerald-600">Volume Payin</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-emerald-600">Fee Payin</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-rose-600">จำนวน Payout</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-rose-600">Volume Payout</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-rose-600">Fee Payout</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-sky-600">Fee P2P</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap">รวม Fee</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-amber-600">ส่วนตัวแทน</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap text-orange-500">ส่วน Parent</th>
                    <th className="px-4 py-3 text-right font-medium whitespace-nowrap">ส่วนเจ้าของ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                  {data.merchants.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-4 py-12 text-center text-gray-400 dark:text-slate-500">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-3xl mb-2 block mx-auto opacity-20" />
                        ไม่มีข้อมูลในช่วงเวลาที่เลือก
                      </td>
                    </tr>
                  ) : (
                    data.merchants.map(m => (
                      <tr key={m.merchant_id} className="hover:bg-gray-50/60 dark:hover:bg-slate-700/30 transition-colors">

                        {/* ร้านค้า */}
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-700 dark:text-slate-200">{m.merchant_name}</div>
                          <div className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                            In {m.fee_percentage}% · Out {m.payout_fee_percentage}%
                          </div>
                        </td>

                        {/* ตัวแทน */}
                        <td className="px-4 py-3">
                          {m.reseller_name ? (
                            <div>
                              <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">
                                <FontAwesomeIcon icon={faHandshake} className="text-xs" />
                                {m.reseller_name}
                              </span>
                              {m.parent_reseller_name && (
                                <div className="mt-0.5">
                                  <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800 rounded-full px-1.5 py-0.5">
                                    ↑ {m.parent_reseller_name}
                                  </span>
                                </div>
                              )}
                              <div className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                                {m.reseller_fee_rate}% / {m.reseller_payout_fee_rate}%
                                {m.parent_reseller_name && (
                                  <span className="text-orange-400"> (Parent: {m.parent_fee_rate}%/{m.parent_payout_fee_rate}%)</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-slate-500">Direct</span>
                          )}
                        </td>

                        {/* Payin */}
                        <td className="px-4 py-3 text-right text-gray-500 dark:text-slate-400 whitespace-nowrap">
                          {fmtInt(m.payin_count)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300 whitespace-nowrap">
                          ฿{fmt(m.payin_volume)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                          ฿{fmt(m.payin_fee)}
                        </td>

                        {/* Payout */}
                        <td className="px-4 py-3 text-right text-gray-500 dark:text-slate-400 whitespace-nowrap">
                          {fmtInt(m.payout_count)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300 whitespace-nowrap">
                          ฿{fmt(m.payout_volume)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-rose-600 dark:text-rose-400 whitespace-nowrap">
                          ฿{fmt(m.payout_fee)}
                        </td>

                        {/* Fee P2P (หักจากกระเป๋า fee) */}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {(m.p2p_fee || 0) > 0 ? (
                            <div>
                              <div className="font-semibold text-sky-600 dark:text-sky-400">฿{fmt(m.p2p_fee || 0)}</div>
                              <div className="text-xs text-gray-400 dark:text-slate-500">In ฿{fmt(m.p2p_fee_payin || 0)} · Out ฿{fmt(m.p2p_fee_payout || 0)}</div>
                            </div>
                          ) : (
                            <span className="text-gray-300 dark:text-slate-600">—</span>
                          )}
                        </td>

                        {/* รวม Fee */}
                        <td className="px-4 py-3 text-right font-bold text-violet-600 dark:text-violet-400 whitespace-nowrap">
                          ฿{fmt(m.total_fee)}
                        </td>

                        {/* Reseller (sub) share */}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {m.total_reseller_share > 0 ? (
                            <div>
                              <div className="font-semibold text-amber-600 dark:text-amber-400">฿{fmt(m.total_reseller_share)}</div>
                              <div className="text-xs text-gray-400 dark:text-slate-500">{pct(m.total_reseller_share, m.total_fee)}</div>
                            </div>
                          ) : (
                            <span className="text-gray-300 dark:text-slate-600">—</span>
                          )}
                        </td>

                        {/* Parent reseller spread */}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {(m.total_parent_share || 0) > 0 ? (
                            <div>
                              <div className="font-semibold text-orange-500 dark:text-orange-400">฿{fmt(m.total_parent_share)}</div>
                              <div className="text-xs text-gray-400 dark:text-slate-500">{pct(m.total_parent_share, m.total_fee)}</div>
                            </div>
                          ) : (
                            <span className="text-gray-300 dark:text-slate-600">—</span>
                          )}
                        </td>

                        {/* Owner (platform) share */}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="font-semibold text-teal-600 dark:text-teal-400">
                            ฿{fmt(m.total_owner_share)}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-slate-500">{pct(m.total_owner_share, m.total_fee)}</div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {/* Footer totals */}
                {data.merchants.length > 0 && s && (
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 dark:border-slate-600 bg-gray-50/80 dark:bg-slate-700/40 text-sm font-semibold">
                      <td className="px-4 py-3 text-gray-600 dark:text-slate-300" colSpan={2}>
                        รวม ({fmtInt(s.merchant_count)} ร้านค้า)
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300">{fmtInt(s.total_payin_count)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300">฿{fmt(s.total_payin_volume)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600 dark:text-emerald-400">฿{fmt(s.total_payin_fee)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300">{fmtInt(s.total_payout_count)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-slate-300">฿{fmt(s.total_payout_volume)}</td>
                      <td className="px-4 py-3 text-right text-rose-600 dark:text-rose-400">฿{fmt(s.total_payout_fee)}</td>
                      <td className="px-4 py-3 text-right text-sky-600 dark:text-sky-400">฿{fmt(s.total_p2p_fee || 0)}</td>
                      <td className="px-4 py-3 text-right font-bold text-violet-600 dark:text-violet-400">
                        ฿{fmt(s.total_fee)}
                      </td>
                      <td className="px-4 py-3 text-right text-amber-600 dark:text-amber-400">฿{fmt(s.total_reseller_share)}</td>
                      <td className="px-4 py-3 text-right text-orange-500 dark:text-orange-400">
                        {(s.total_parent_share || 0) > 0 ? `฿${fmt(s.total_parent_share)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-teal-600 dark:text-teal-400">
                        ฿{fmt(s.total_owner_share)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* ── Tab: รายตัวแทน ── */}
          {tab === 'reseller' && (
            <div>
              {data.by_reseller.length === 0 && !s?.direct_merchant_count ? (
                <div className="px-4 py-12 text-center text-gray-400 dark:text-slate-500">
                  <FontAwesomeIcon icon={faHandshake} className="text-3xl mb-2 block mx-auto opacity-20" />
                  ไม่มีข้อมูลตัวแทนในช่วงเวลาที่เลือก
                </div>
              ) : (
                <div>
                  {/* Reseller rows */}
                  {(() => {
                    const parentSpreadMap = Object.fromEntries(
                      (data.by_parent_reseller || []).map(p => [p.reseller_id, p])
                    );
                    return data.by_reseller.map(r => {
                    const expanded = expandedResellers.has(r.reseller_id);
                    const subMerchants = data.merchants.filter(m => m.reseller_id === r.reseller_id);
                    const isSub = !!r.parent_reseller_id;
                    const spread = !isSub ? (parentSpreadMap[r.reseller_id] || null) : null;
                    const totalResellerIncome = r.total_reseller_share + (spread?.total_spread || 0);
                    return (
                      <div key={r.reseller_id} className={`border-b border-gray-100 dark:border-slate-700 last:border-0 ${isSub ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''}`}>
                        <div
                          role="button"
                          tabIndex={0}
                          className="flex flex-col sm:flex-row sm:items-center gap-4 px-4 py-4 cursor-pointer hover:bg-gray-50/60 dark:hover:bg-slate-700/30 transition-colors"
                          onClick={() => toggleReseller(r.reseller_id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleReseller(r.reseller_id);
                            }
                          }}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FontAwesomeIcon
                              icon={expanded ? faChevronDown : faChevronRight}
                              className="text-gray-400 dark:text-slate-500 w-4 shrink-0 text-xs"
                            />
                            <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                              <FontAwesomeIcon icon={faHandshake} className="text-amber-500 dark:text-amber-400 text-sm" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700 dark:text-slate-200">{r.reseller_name}</span>
                                {isSub && (
                                  <span className="text-[10px] font-bold bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800 rounded-full px-1.5 py-0.5">Sub</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-400 dark:text-slate-500">
                                {fmtInt(r.merchant_count)} ร้านค้า
                                {isSub && r.parent_reseller_name && (
                                  <span className="ml-1 text-orange-400">↑ {r.parent_reseller_name}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-6 gap-y-1 text-right text-sm ml-10 sm:ml-0">
                            <div>
                              <div className="text-[10px] text-emerald-500 font-medium uppercase">Payin Fee</div>
                              <div className="font-semibold text-emerald-600 dark:text-emerald-400">฿{fmt(r.payin_fee)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-rose-500 dark:text-rose-400 font-medium uppercase">Payout Fee</div>
                              <div className="font-semibold text-rose-600 dark:text-rose-400">฿{fmt(r.payout_fee)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-gray-400 dark:text-slate-500 font-medium uppercase">รวม Fee</div>
                              <div className="font-bold text-violet-600 dark:text-violet-400">฿{fmt(r.total_fee)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-amber-500 dark:text-amber-400 font-medium uppercase">ส่วนตัวแทน</div>
                              <div className="font-bold text-amber-600 dark:text-amber-400">฿{fmt(totalResellerIncome)}</div>
                              {isSub && (r.total_parent_share || 0) > 0 && (
                                <div className="text-[10px] text-orange-500">Parent: ฿{fmt(r.total_parent_share)}</div>
                              )}
                              {spread && spread.total_spread > 0 && (
                                <div className="text-[10px] text-orange-500">
                                  Direct ฿{fmt(r.total_reseller_share)} +Sub ฿{fmt(spread.total_spread)}
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="text-[10px] text-teal-600 dark:text-teal-400 font-medium uppercase">ส่วนเจ้าของ</div>
                              <div className="font-bold text-teal-600 dark:text-teal-400">฿{fmt(r.total_owner_share)}</div>
                              <div className="text-[10px] text-gray-400 dark:text-slate-500">{pct(r.total_owner_share, r.total_fee)}</div>
                            </div>
                          </div>
                        </div>

                        {/* Expanded sub-merchants */}
                        {expanded && (
                          <div className="bg-gray-50/60 dark:bg-slate-700/30 border-t border-gray-100 dark:border-slate-700">
                            <div className="w-full overflow-x-auto">
                              <table className="w-full text-xs table-auto">
                                <thead>
                                  <tr className="text-gray-400 dark:text-slate-500 uppercase tracking-wider border-b border-gray-200 dark:border-slate-600">
                                    <th className="px-6 py-2 text-left font-medium">ร้านค้า</th>
                                    <th className="px-4 py-2 text-right font-medium text-emerald-500">Payin</th>
                                    <th className="px-4 py-2 text-right font-medium text-emerald-500">Fee Payin</th>
                                    <th className="px-4 py-2 text-right font-medium text-rose-500">Payout</th>
                                    <th className="px-4 py-2 text-right font-medium text-rose-500">Fee Payout</th>
                                    <th className="px-4 py-2 text-right font-medium">รวม Fee</th>
                                    <th className="px-4 py-2 text-right font-medium text-amber-500">ส่วนตัวแทน</th>
                                    <th className="px-4 py-2 text-right font-medium">ส่วนเจ้าของ</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                  {subMerchants.map(m => (
                                    <tr key={m.merchant_id} className="hover:bg-white dark:hover:bg-slate-800 transition-colors">
                                      <td className="px-6 py-2">
                                        <div className="font-medium text-gray-600 dark:text-slate-300">{m.merchant_name}</div>
                                        <div className="text-gray-400 dark:text-slate-500">In {m.fee_percentage}% · Out {m.payout_fee_percentage}%</div>
                                      </td>
                                      <td className="px-4 py-2 text-right text-gray-500 dark:text-slate-400">{fmtInt(m.payin_count)}</td>
                                      <td className="px-4 py-2 text-right font-medium text-emerald-600 dark:text-emerald-400">฿{fmt(m.payin_fee)}</td>
                                      <td className="px-4 py-2 text-right text-gray-500 dark:text-slate-400">{fmtInt(m.payout_count)}</td>
                                      <td className="px-4 py-2 text-right font-medium text-rose-600 dark:text-rose-400">฿{fmt(m.payout_fee)}</td>
                                      <td className="px-4 py-2 text-right font-bold text-violet-600 dark:text-violet-400">
                                        ฿{fmt(m.total_fee)}
                                      </td>
                                      <td className="px-4 py-2 text-right font-semibold text-amber-600 dark:text-amber-400">
                                        ฿{fmt(m.total_reseller_share)}
                                      </td>
                                      <td className="px-4 py-2 text-right font-semibold text-teal-600 dark:text-teal-400">
                                        ฿{fmt(m.total_owner_share)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  });
                  })()}

                  {/* Direct merchants row */}
                  {s && s.direct_merchant_count > 0 && (
                    <div className="border-t border-dashed border-gray-200 dark:border-slate-600 px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-4 shrink-0" />
                        <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center shrink-0">
                          <FontAwesomeIcon icon={faBuilding} className="text-gray-400 dark:text-slate-500 text-sm" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-500 dark:text-slate-400">Direct Merchants</div>
                          <div className="text-xs text-gray-400 dark:text-slate-500">{fmtInt(s.direct_merchant_count)} ร้านค้า (ไม่มีตัวแทน)</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-6 text-right text-sm ml-10 sm:ml-0">
                        <div />
                        <div />
                        <div>
                          <div className="text-[10px] text-gray-400 dark:text-slate-500 font-medium uppercase">รวม Fee</div>
                          <div className="font-bold text-violet-600 dark:text-violet-400">฿{fmt(directFee)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-amber-500 dark:text-amber-400 font-medium uppercase">ส่วนตัวแทน</div>
                          <div className="font-bold text-gray-300 dark:text-slate-600">—</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-teal-600 dark:text-teal-400 font-medium uppercase">ส่วนเจ้าของ</div>
                          <div className="font-bold text-teal-600 dark:text-teal-400">฿{fmt(directFee)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grand total footer */}
                  {s && (
                    <div className="border-t-2 border-gray-200 dark:border-slate-600 bg-gray-50/80 dark:bg-slate-700/40 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1 font-semibold text-gray-600 dark:text-slate-300 ml-16">รวมทั้งหมด</div>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-6 text-right text-sm ml-10 sm:ml-0">
                        <div>
                          <div className="text-[10px] text-emerald-500 dark:text-emerald-400 font-medium uppercase">Payin Fee</div>
                          <div className="font-semibold text-emerald-600 dark:text-emerald-400">฿{fmt(s.total_payin_fee)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-rose-500 dark:text-rose-400 font-medium uppercase">Payout Fee</div>
                          <div className="font-semibold text-rose-600 dark:text-rose-400">฿{fmt(s.total_payout_fee)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 dark:text-slate-500 font-medium uppercase">รวม Fee</div>
                          <div className="font-bold text-violet-600 dark:text-violet-400">฿{fmt(s.total_fee)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-amber-500 dark:text-amber-400 font-medium uppercase">ส่วนตัวแทน (รวม)</div>
                          <div className="font-bold text-amber-600 dark:text-amber-400">฿{fmt(s.total_reseller_share + (s.total_parent_share || 0))}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-teal-600 dark:text-teal-400 font-medium uppercase">ส่วนเจ้าของ</div>
                          <div className="font-bold text-teal-600 dark:text-teal-400">฿{fmt(s.total_owner_share)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Empty state (before first search) ── */}
      {!data && !loading && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme border border-gray-100 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-violet-50 dark:bg-violet-900/30">
            <FontAwesomeIcon icon={faPercent} className="text-2xl text-violet-600 dark:text-violet-400" />
          </div>
          <p className="font-semibold text-gray-600 dark:text-slate-300">เลือกช่วงเวลาแล้วกด "ค้นหา"</p>
          <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">
            แสดงค่าธรรมเนียม Payin / Payout แยกตามร้านค้าและตัวแทน
          </p>
        </div>
      )}
    </div>
  );
};

export default FeeReportPage;
